import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserGroupIcon, 
  BuildingOfficeIcon, 
  ShieldCheckIcon, 
  UserPlusIcon, 
  IdentificationIcon,
  ChevronRightIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Administration = () => {
  const navigate = useNavigate();
  const [showAllLogs, setShowAllLogs] = useState(false);

  // Stats Data
  const stats = [
    { 
      label: 'Total Employees', 
      value: '1,248', 
      color: 'text-blue-600', 
      hover: 'hover:bg-blue-600 hover:text-white hover:border-blue-600' 
    },
    { 
      label: 'Active Employees', 
      value: '1,192', 
      color: 'text-green-600', 
      hover: 'hover:bg-green-600 hover:text-white hover:border-green-600' 
    },
    { 
      label: 'Pending Onboarding', 
      value: '14', 
      color: 'text-amber-600', 
      hover: 'hover:bg-amber-500 hover:text-white hover:border-amber-500' 
    },
    { 
      label: 'Admin Users', 
      value: '8', 
      color: 'text-purple-600', 
      hover: 'hover:bg-purple-600 hover:text-white hover:border-purple-600' 
    },
  ];

  const adminModules = [
    { name: 'Admin Overview', desc: 'System health, admin activity & security status', icon: ShieldCheckIcon, path: '/admin/admin-overview', color: 'text-indigo-600' },
    { name: 'Employee Management', desc: 'View and manage all company employees', icon: UserGroupIcon, path: '/admin/people-management', color: 'text-sky-600' },
    { name: 'Employee Setup', desc: 'Configure onboarding, access & defaults', icon: UserPlusIcon, path: '/admin/employee-setup', color: 'text-emerald-600' },
    { name: 'Super Admin Profile ', desc: 'Admin-level employee governance & access', icon: IdentificationIcon, path: '/admin/employee-profile-admin', color: 'text-rose-600' },
    { name: 'Company Profile', desc: 'Company identity, compliance & subscription', icon: BuildingOfficeIcon, path: '/admin/company-profile', color: 'text-slate-600' },
  ];

  // Extended Activity Data
  const activities = [
    { user: 'Rahul Sharma', action: 'Updated Compliance Policy', time: '10:15 AM, 07 Jan', status: 'Success' },
    { user: 'Ananya Iyer', action: 'Bulk Import: 12 Employees', time: '09:30 AM, 07 Jan', status: 'Success' },
    { user: 'System Bot', action: 'Automated Backup', time: '02:00 AM, 07 Jan', status: 'Success' },
    { user: 'Kevin Peterson', action: 'Modified Admin Permissions', time: '05:45 PM, 06 Jan', status: 'Warning' },
    { user: 'Arjun Mehta', action: 'Exported Payroll Report', time: '04:20 PM, 06 Jan', status: 'Success' },
    { user: 'Priya Sharma', action: 'Added New Department: R&D', time: '02:15 PM, 06 Jan', status: 'Success' },
    { user: 'System Bot', action: 'Security Patch Applied', time: '01:00 AM, 06 Jan', status: 'Success' },
  ];

  // Logic to show only 4 or all logs
  const visibleActivities = showAllLogs ? activities : activities.slice(0, 4);

  return (
    <div className="min-h-screen bg-slate-50 p-4 font-roboto text-slate-800">
      
      {/* 1. PAGE HEADER */}
      <header className="flex flex-row items-center justify-between mb-6 border-b border-slate-200 pb-4">
        <div>
          <h1 className="font-poppins text-xl font-bold text-slate-900 tracking-tight">Administration</h1>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-black">Super Admin Control Center</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => navigate('/admin/people-management')}
            className="px-3 py-1.5 text-xs font-black border border-slate-300 rounded bg-white hover:bg-slate-50 transition-colors shadow-sm"
          >
            People Management
          </button>
          <button 
            onClick={() => navigate('/admin/company-profile')}
            className="px-3 py-1.5 text-xs font-black bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors shadow-sm"
          >
            Company Profile
          </button>
        </div>
      </header>

      {/* 2. ADMIN STATISTICS BAR */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {stats.map((stat, idx) => (
          <div 
            key={idx} 
            className={`bg-white border border-slate-200 p-4 rounded shadow-sm flex flex-col cursor-default transition-all duration-300 group ${stat.hover}`}
          >
            <span className="text-[11px] font-black uppercase tracking-widest leading-none mb-2 transition-colors duration-300">
              {stat.label}
            </span>
            <span className={`text-2xl font-black transition-colors duration-300 ${stat.color} group-hover:text-white`}>
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      {/* 3. ADMIN MODULE NAVIGATION */}
      <section className="mb-8">
        <h2 className="font-poppins text-sm font-bold text-slate-700 mb-3 uppercase tracking-tight">Management Modules</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {adminModules.map((module, idx) => (
            <div 
              key={idx}
              onClick={() => navigate(module.path)}
              className="group cursor-pointer bg-white border border-slate-200 p-3 rounded flex items-start gap-4 hover:border-indigo-400 hover:shadow-md transition-all duration-200 relative"
            >
              <div className={`p-2 rounded ${module.color} bg-opacity-10 group-hover:scale-110 transition-transform`}>
                <module.icon className={`w-5 h-5 ${module.color}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-black text-slate-800 group-hover:text-indigo-600 transition-colors">
                  {module.name}
                </h3>
                <p className="text-[11px] text-slate-500 font-bold leading-relaxed mt-0.5">
                  {module.desc}
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 mt-1" />
            </div>
          ))}
        </div>
      </section>

      {/* 4. RECENT ADMIN ACTIVITY */}
      <section className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-slate-500" />
          <h2 className="font-poppins text-xs font-bold text-slate-700 uppercase">Recent Admin Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white border-b border-slate-100">
                <th className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">Admin User</th>
                <th className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">Action</th>
                <th className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">Date & Time</th>
                <th className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-wider text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleActivities.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-4 py-2 text-xs font-black text-slate-700">{item.user}</td>
                  <td className="px-4 py-2 text-xs font-black text-slate-600">{item.action}</td>
                  <td className="px-4 py-2 text-[11px] font-black text-slate-500 font-mono uppercase">{item.time}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter ${
                      item.status === 'Success' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Functional View All Button */}
        <div className="p-2 border-t border-slate-100 text-center">
            <button 
              onClick={() => setShowAllLogs(!showAllLogs)}
              className="text-[10px] font-black text-indigo-600 hover:text-indigo-800 uppercase tracking-widest transition-all"
            >
              {showAllLogs ? 'Show Less' : 'View All Activity Log'}
            </button>
        </div>
      </section>

    </div>
  );
};

export default Administration;